/**
 * VanguardHoldingsTable Component
 *
 * A professional table-based holdings display inspired by Vanguard's interface.
 * Displays investments in a clean tabular format with sortable columns.
 */

import React, { useState, useMemo } from 'react';
import { ChevronUp, ChevronDown, Plus, Edit2, Trash2, X, PieChart } from 'lucide-react';
import { useCountry } from '../../CountryContext';
import { getCurrencyCode, getCurrencySymbol } from '../../countryConfig';
import { usePortfolioPrices } from '../../hooks/usePricing';
import { formatCurrency } from '../../utils/priceCalculations';
import SectorRegionInputModal from './SectorRegionInputModal';

const VanguardHoldingsTable = ({
  investments = [],
  accounts = [],
  exchangeRates = null,
  isDark = false,
  onEditInvestment,
  onAddToPosition,
  onDeleteInvestment,
  onUpdateInvestmentSectorRegion
}) => {
  const { selectedCountry } = useCountry();
  const displayCurrency = getCurrencyCode(selectedCountry);
  const currencySymbol = getCurrencySymbol(selectedCountry);

  // Fetch real-time prices for all investments
  const { prices } = usePortfolioPrices(investments);

  // Sort state
  const [sortField, setSortField] = useState('currentValue');
  const [sortDirection, setSortDirection] = useState('desc');
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [selectedInvestmentId, setSelectedInvestmentId] = useState(null);

  // Sector/Region modal state
  const [sectorRegionModalOpen, setSectorRegionModalOpen] = useState(false);
  const [sectorRegionInvestment, setSectorRegionInvestment] = useState(null);

  // Process and group investments by category
  const { groupedInvestments, totalPortfolioValue, totalCost, totalChange } = useMemo(() => {
    const groups = {};
    let portfolioTotal = 0;
    let costTotal = 0;
    let changeTotal = 0;

    investments.forEach(inv => {
      const category = inv.category || 'Other';
      if (!groups[category]) {
        groups[category] = {
          name: category,
          investments: [],
          totalValue: 0,
          totalCost: 0,
          totalChange: 0
        };
      }

      // Get live price data - check ticker first, then ISIN
      const priceData = inv.ticker ? prices[inv.ticker] : (inv.isin ? prices[inv.isin] : null);
      const hasLivePrice = priceData?.price && priceData.price > 0;

      // Calculate current value and cost basis based on investment type
      let currentValue = 0;
      let costBasis = 0;
      let currentPrice = 0;
      let avgCost = 0;
      const quantity = inv.quantity || 0;

      if (quantity > 0) {
        // Quantity-tracked investment
        avgCost = inv.averageCostBasis || inv.pricePerUnit || 0;

        // Determine current price
        if (hasLivePrice) {
          currentPrice = priceData.price;
        } else if (inv.pricePerUnit && inv.pricePerUnit > 0) {
          currentPrice = inv.pricePerUnit; // Use last known price
        } else {
          currentPrice = avgCost; // Fall back to avg cost (no gain/loss)
        }

        currentValue = quantity * currentPrice;
        costBasis = quantity * avgCost;
      } else {
        // Amount-only investment (legacy or simple tracking)
        currentValue = inv.amount || 0;
        costBasis = inv.totalCostBasis || inv.amount || 0;
        // For display purposes, treat as single unit
        currentPrice = currentValue;
        avgCost = costBasis;
      }

      // Convert to display currency if needed
      const invCurrency = inv.currency || displayCurrency;
      if (invCurrency !== displayCurrency && exchangeRates?.rates) {
        const fromRate = exchangeRates.rates[invCurrency] || 1;
        const toRate = exchangeRates.rates[displayCurrency] || 1;
        const conversionRate = toRate / fromRate;
        currentValue *= conversionRate;
        costBasis *= conversionRate;
        currentPrice *= conversionRate;
        avgCost *= conversionRate;
      }

      const change = currentValue - costBasis;
      const changePercent = costBasis > 0 ? (change / costBasis) * 100 : 0;

      const processedInv = {
        ...inv,
        quantity,
        avgCost,
        currentPrice,
        costBasis,
        currentValue,
        change,
        changePercent,
        hasLivePrice
      };

      groups[category].investments.push(processedInv);
      groups[category].totalValue += currentValue;
      groups[category].totalCost += costBasis;
      groups[category].totalChange += change;

      portfolioTotal += currentValue;
      costTotal += costBasis;
      changeTotal += change;
    });

    // Calculate weights after we have total
    Object.values(groups).forEach(group => {
      group.weight = portfolioTotal > 0 ? (group.totalValue / portfolioTotal) * 100 : 0;
      group.investments.forEach(inv => {
        inv.weight = portfolioTotal > 0 ? (inv.currentValue / portfolioTotal) * 100 : 0;
      });
    });

    return {
      groupedInvestments: groups,
      totalPortfolioValue: portfolioTotal,
      totalCost: costTotal,
      totalChange: changeTotal
    };
  }, [investments, prices, displayCurrency, exchangeRates]);

  // Sort investments within each category
  const sortedGroups = useMemo(() => {
    const sorted = {};
    Object.entries(groupedInvestments).forEach(([category, group]) => {
      sorted[category] = {
        ...group,
        investments: [...group.investments].sort((a, b) => {
          let aVal = a[sortField] || 0;
          let bVal = b[sortField] || 0;
          if (typeof aVal === 'string') {
            return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
          }
          return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
        })
      };
    });
    return sorted;
  }, [groupedInvestments, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handleOpenSectorRegionModal = (inv) => {
    // Find the original investment to get full data including existing sector/region data
    const originalInv = investments.find(i => i.id === inv.id);
    setSectorRegionInvestment(originalInv || inv);
    setSectorRegionModalOpen(true);
  };

  const handleSaveSectorRegion = (data) => {
    if (onUpdateInvestmentSectorRegion && sectorRegionInvestment) {
      onUpdateInvestmentSectorRegion(sectorRegionInvestment.id, data);
    }
    setSectorRegionModalOpen(false);
    setSectorRegionInvestment(null);
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc'
      ? <ChevronUp className="w-3 h-3 inline ml-1" />
      : <ChevronDown className="w-3 h-3 inline ml-1" />;
  };

  const formatValue = (value) => formatCurrency(value, displayCurrency, 2);

  const formatQuantity = (qty) => {
    if (!qty || qty <= 0) return '-';
    return qty.toFixed(4);
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Stocks': 'bg-blue-500',
      'ETF': 'bg-green-500',
      'Bonds': 'bg-purple-500',
      'Cryptocurrency': 'bg-orange-500',
      'Real Estate': 'bg-teal-500',
      'Cash': 'bg-yellow-500',
      'Commodities': 'bg-amber-500',
      'Other': 'bg-gray-500'
    };
    return colors[category] || 'bg-gray-500';
  };

  // Find selected investment from groups
  const selectedInvestment = useMemo(() => {
    if (!selectedInvestmentId) return null;
    for (const group of Object.values(sortedGroups)) {
      const found = group.investments.find(inv => inv.id === selectedInvestmentId);
      if (found) return found;
    }
    return null;
  }, [selectedInvestmentId, sortedGroups]);

  // Render detail panel for selected investment
  const renderDetailPanel = (inv) => (
    <tr key={`detail-${inv.id}`}>
      <td colSpan={7} className={`p-0 ${isDark ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
        <div className={`p-6 border-t-2 border-b-2 ${isDark ? 'border-blue-700' : 'border-blue-300'}`}>
          <div className="flex flex-col md:flex-row md:items-start justify-between mb-4 gap-4">
            <div>
              <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {inv.name}
              </h3>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                {inv.category}
                {inv.ticker && ` • ${inv.ticker}`}
                {inv.isin && !inv.ticker && ` • ISIN: ${inv.isin}`}
                {inv.accountName && ` • ${inv.accountName}`}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (onAddToPosition) {
                    onAddToPosition(inv);
                  } else {
                    console.warn('onAddToPosition handler not provided');
                  }
                }}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium ${isDark ? 'bg-green-900/50 text-green-400 hover:bg-green-900' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}
              >
                <Plus className="w-4 h-4" /> Add
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (onEditInvestment) {
                    onEditInvestment(inv);
                  } else {
                    console.warn('onEditInvestment handler not provided');
                  }
                }}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium ${isDark ? 'bg-blue-900/50 text-blue-400 hover:bg-blue-900' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`}
              >
                <Edit2 className="w-4 h-4" /> Edit
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleOpenSectorRegionModal(inv);
                }}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium ${isDark ? 'bg-purple-900/50 text-purple-400 hover:bg-purple-900' : 'bg-purple-100 text-purple-700 hover:bg-purple-200'}`}
                title="Edit sector and region allocation data"
              >
                <PieChart className="w-4 h-4" /> Sectors
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (onDeleteInvestment) {
                    if (window.confirm(`Delete "${inv.name}"?`)) {
                      onDeleteInvestment(inv.id);
                      setSelectedInvestmentId(null);
                    }
                  } else {
                    console.warn('onDeleteInvestment handler not provided');
                  }
                }}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium ${isDark ? 'bg-red-900/50 text-red-400 hover:bg-red-900' : 'bg-red-100 text-red-700 hover:bg-red-200'}`}
              >
                <Trash2 className="w-4 h-4" /> Delete
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedInvestmentId(null);
                }}
                className={`p-1.5 rounded-lg ${isDark ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-200'}`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
              <p className={`text-xs font-medium uppercase ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Quantity</p>
              <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {formatQuantity(inv.quantity)}
              </p>
            </div>
            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
              <p className={`text-xs font-medium uppercase ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Average Cost</p>
              <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {inv.quantity > 0 ? formatValue(inv.avgCost) : '-'}
              </p>
            </div>
            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
              <p className={`text-xs font-medium uppercase ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Current Price {inv.hasLivePrice && <span className="text-green-500 text-xs">• Live</span>}
              </p>
              <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {inv.quantity > 0 ? formatValue(inv.currentPrice) : '-'}
              </p>
            </div>
            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
              <p className={`text-xs font-medium uppercase ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Cost Basis</p>
              <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {formatValue(inv.costBasis)}
              </p>
            </div>
            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
              <p className={`text-xs font-medium uppercase ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Market Value</p>
              <p className={`text-xl font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                {formatValue(inv.currentValue)}
              </p>
            </div>
            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
              <p className={`text-xs font-medium uppercase ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Gain/Loss</p>
              <p className={`text-xl font-bold ${inv.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {inv.change >= 0 ? '+' : ''}{formatValue(inv.change)}
                <span className="text-sm ml-1">({inv.changePercent >= 0 ? '+' : ''}{inv.changePercent.toFixed(2)}%)</span>
              </p>
            </div>
            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
              <p className={`text-xs font-medium uppercase ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Weight</p>
              <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {inv.weight.toFixed(2)}%
              </p>
            </div>
            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
              <p className={`text-xs font-medium uppercase ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Risk</p>
              <p className={`text-lg font-bold ${
                inv.risk === 'High' ? 'text-red-600' :
                inv.risk === 'Medium' ? 'text-yellow-600' : 'text-green-600'
              }`}>
                {inv.risk || 'Medium'}
              </p>
            </div>
          </div>

          {inv.purchaseDate && (
            <div className={`mt-4 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              First purchased: {new Date(inv.purchaseDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
              {inv.lastPurchaseDate && inv.lastPurchaseDate !== inv.purchaseDate && (
                <span> • Last: {new Date(inv.lastPurchaseDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
              )}
              {inv.purchaseCount > 1 && <span> • {inv.purchaseCount} purchases</span>}
            </div>
          )}
        </div>
      </td>
    </tr>
  );

  return (
    <div className={`rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
      {/* Mobile scroll hint */}
      <div className={`px-3 py-2 md:hidden text-xs text-center ${isDark ? 'bg-gray-900/30 text-gray-400' : 'bg-gray-50 text-gray-600'} border-b ${isDark ? 'border-gray-700' : 'border-gray-200'} rounded-t-xl`}>
        ← Scroll horizontally to see all columns →
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px]">
          <thead>
            <tr className={`border-b ${isDark ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-gray-50'}`}>
              <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-80 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} onClick={() => handleSort('name')}>
                Holdings <SortIcon field="name" />
              </th>
              <th className={`px-3 py-3 text-right text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-80 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} onClick={() => handleSort('quantity')}>
                My Units <SortIcon field="quantity" />
              </th>
              <th className={`px-3 py-3 text-right text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-80 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} onClick={() => handleSort('avgCost')}>
                Avg Cost <SortIcon field="avgCost" />
              </th>
              <th className={`px-3 py-3 text-right text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-80 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} onClick={() => handleSort('currentPrice')}>
                Last Price <SortIcon field="currentPrice" />
              </th>
              <th className={`px-3 py-3 text-right text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-80 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} onClick={() => handleSort('costBasis')}>
                Total Cost <SortIcon field="costBasis" />
              </th>
              <th className={`px-3 py-3 text-right text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-80 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} onClick={() => handleSort('currentValue')}>
                Value <SortIcon field="currentValue" />
              </th>
              <th className={`px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-80 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} onClick={() => handleSort('change')}>
                Change <SortIcon field="change" />
              </th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(sortedGroups).map(([category, group]) => (
              <React.Fragment key={category}>
                {/* Category Header Row */}
                <tr
                  className={`${isDark ? 'bg-gray-800/50' : 'bg-gray-100/50'} cursor-pointer hover:opacity-90`}
                  onClick={() => setExpandedCategory(expandedCategory === category ? null : category)}
                >
                  <td colSpan={7} className="px-4 py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${getCategoryColor(category)}`} />
                        <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{category}</span>
                        <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>({group.weight.toFixed(0)}%)</span>
                      </div>
                      <div className="flex items-center gap-6">
                        <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatValue(group.totalValue)}</span>
                        <span className={`font-semibold ${group.totalChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {group.totalChange >= 0 ? '+' : ''}{formatValue(group.totalChange)}
                        </span>
                        {expandedCategory === category
                          ? <ChevronUp className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
                          : <ChevronDown className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
                        }
                      </div>
                    </div>
                  </td>
                </tr>

                {/* Individual Holdings Rows */}
                {(expandedCategory === category || expandedCategory === null) && group.investments.map((inv) => (
                  <React.Fragment key={inv.id}>
                    <tr className={`border-b ${isDark ? 'border-gray-700 hover:bg-gray-700/50' : 'border-gray-100 hover:bg-gray-50'} transition-colors ${selectedInvestmentId === inv.id ? (isDark ? 'bg-blue-900/30' : 'bg-blue-50') : ''}`}>
                      <td className="px-4 py-3">
                        <div>
                          <button
                            className={`font-medium hover:underline text-left ${isDark ? 'text-blue-400' : 'text-blue-600'} ${selectedInvestmentId === inv.id ? 'underline' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedInvestmentId(selectedInvestmentId === inv.id ? null : inv.id);
                            }}
                          >
                            {inv.name}
                          </button>
                          <div className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            Weight: {inv.weight.toFixed(2)}%
                          </div>
                        </div>
                      </td>
                      <td className={`px-3 py-3 text-right text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        {formatQuantity(inv.quantity)}
                      </td>
                      <td className={`px-3 py-3 text-right text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        {inv.quantity > 0 ? formatValue(inv.avgCost) : '-'}
                      </td>
                      <td className={`px-3 py-3 text-right text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        {inv.quantity > 0 ? formatValue(inv.currentPrice) : '-'}
                        {inv.hasLivePrice && <span className="ml-1 text-green-500 text-xs">●</span>}
                      </td>
                      <td className={`px-3 py-3 text-right text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        {formatValue(inv.costBasis)}
                      </td>
                      <td className={`px-3 py-3 text-right text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {formatValue(inv.currentValue)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`text-sm font-semibold ${inv.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {inv.change >= 0 ? '+' : ''}{formatValue(inv.change)}
                        </span>
                      </td>
                    </tr>
                    {/* Inline detail panel right after the investment row */}
                    {selectedInvestmentId === inv.id && renderDetailPanel(inv)}
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))}
          </tbody>

          {/* Totals Footer */}
          <tfoot>
            <tr className={`font-semibold ${isDark ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-900'}`}>
              <td className="px-4 py-4 text-sm font-bold">Total holdings</td>
              <td className="px-3 py-4 text-right text-sm">-</td>
              <td className="px-3 py-4 text-right text-sm">-</td>
              <td className="px-3 py-4 text-right text-sm">-</td>
              <td className="px-3 py-4 text-right text-sm">{formatValue(totalCost)}</td>
              <td className="px-3 py-4 text-right text-sm font-bold">{formatValue(totalPortfolioValue)}</td>
              <td className="px-4 py-4 text-right">
                <span className={`text-sm font-bold ${totalChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {totalChange >= 0 ? '+' : ''}{formatValue(totalChange)}
                </span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Sector/Region Input Modal */}
      <SectorRegionInputModal
        isOpen={sectorRegionModalOpen}
        onClose={() => {
          setSectorRegionModalOpen(false);
          setSectorRegionInvestment(null);
        }}
        onSave={handleSaveSectorRegion}
        investment={sectorRegionInvestment}
        isDark={isDark}
      />
    </div>
  );
};

export default VanguardHoldingsTable;
